/**
 * Every ISO 3166-1 alpha-2 territory with its international dialling code, as
 * `[code, name, dialCode]` tuples. One list, so the country select and the phone
 * prefix select can't drift apart.
 *
 * Names follow the ISO short names (what the signup API stores). Codes that
 * share a dial code (+1 across North America and the Caribbean, +44 for the
 * Crown Dependencies) are listed separately on purpose — the flag and the stored
 * country name differ even where the prefix doesn't.
 */
const COUNTRY_TUPLES = [
  ['AF', 'Afghanistan', '+93'],
  ['AX', 'Åland Islands', '+358'],
  ['AL', 'Albania', '+355'],
  ['DZ', 'Algeria', '+213'],
  ['AS', 'American Samoa', '+1'],
  ['AD', 'Andorra', '+376'],
  ['AO', 'Angola', '+244'],
  ['AI', 'Anguilla', '+1'],
  ['AQ', 'Antarctica', '+672'],
  ['AG', 'Antigua and Barbuda', '+1'],
  ['AR', 'Argentina', '+54'],
  ['AM', 'Armenia', '+374'],
  ['AW', 'Aruba', '+297'],
  ['AU', 'Australia', '+61'],
  ['AT', 'Austria', '+43'],
  ['AZ', 'Azerbaijan', '+994'],
  ['BS', 'Bahamas', '+1'],
  ['BH', 'Bahrain', '+973'],
  ['BD', 'Bangladesh', '+880'],
  ['BB', 'Barbados', '+1'],
  ['BY', 'Belarus', '+375'],
  ['BE', 'Belgium', '+32'],
  ['BZ', 'Belize', '+501'],
  ['BJ', 'Benin', '+229'],
  ['BM', 'Bermuda', '+1'],
  ['BT', 'Bhutan', '+975'],
  ['BO', 'Bolivia', '+591'],
  ['BQ', 'Bonaire, Sint Eustatius and Saba', '+599'],
  ['BA', 'Bosnia and Herzegovina', '+387'],
  ['BW', 'Botswana', '+267'],
  ['BV', 'Bouvet Island', '+47'],
  ['BR', 'Brazil', '+55'],
  ['IO', 'British Indian Ocean Territory', '+246'],
  ['BN', 'Brunei Darussalam', '+673'],
  ['BG', 'Bulgaria', '+359'],
  ['BF', 'Burkina Faso', '+226'],
  ['BI', 'Burundi', '+257'],
  ['CV', 'Cabo Verde', '+238'],
  ['KH', 'Cambodia', '+855'],
  ['CM', 'Cameroon', '+237'],
  ['CA', 'Canada', '+1'],
  ['KY', 'Cayman Islands', '+1'],
  ['CF', 'Central African Republic', '+236'],
  ['TD', 'Chad', '+235'],
  ['CL', 'Chile', '+56'],
  ['CN', 'China', '+86'],
  ['CX', 'Christmas Island', '+61'],
  ['CC', 'Cocos (Keeling) Islands', '+61'],
  ['CO', 'Colombia', '+57'],
  ['KM', 'Comoros', '+269'],
  ['CG', 'Congo', '+242'],
  ['CD', 'Congo, Democratic Republic of the', '+243'],
  ['CK', 'Cook Islands', '+682'],
  ['CR', 'Costa Rica', '+506'],
  ['CI', "Côte d'Ivoire", '+225'],
  ['HR', 'Croatia', '+385'],
  ['CU', 'Cuba', '+53'],
  ['CW', 'Curaçao', '+599'],
  ['CY', 'Cyprus', '+357'],
  ['CZ', 'Czechia', '+420'],
  ['DK', 'Denmark', '+45'],
  ['DJ', 'Djibouti', '+253'],
  ['DM', 'Dominica', '+1'],
  ['DO', 'Dominican Republic', '+1'],
  ['EC', 'Ecuador', '+593'],
  ['EG', 'Egypt', '+20'],
  ['SV', 'El Salvador', '+503'],
  ['GQ', 'Equatorial Guinea', '+240'],
  ['ER', 'Eritrea', '+291'],
  ['EE', 'Estonia', '+372'],
  ['SZ', 'Eswatini', '+268'],
  ['ET', 'Ethiopia', '+251'],
  ['FK', 'Falkland Islands', '+500'],
  ['FO', 'Faroe Islands', '+298'],
  ['FJ', 'Fiji', '+679'],
  ['FI', 'Finland', '+358'],
  ['FR', 'France', '+33'],
  ['GF', 'French Guiana', '+594'],
  ['PF', 'French Polynesia', '+689'],
  ['TF', 'French Southern Territories', '+262'],
  ['GA', 'Gabon', '+241'],
  ['GM', 'Gambia', '+220'],
  ['GE', 'Georgia', '+995'],
  ['DE', 'Germany', '+49'],
  ['GH', 'Ghana', '+233'],
  ['GI', 'Gibraltar', '+350'],
  ['GR', 'Greece', '+30'],
  ['GL', 'Greenland', '+299'],
  ['GD', 'Grenada', '+1'],
  ['GP', 'Guadeloupe', '+590'],
  ['GU', 'Guam', '+1'],
  ['GT', 'Guatemala', '+502'],
  ['GG', 'Guernsey', '+44'],
  ['GN', 'Guinea', '+224'],
  ['GW', 'Guinea-Bissau', '+245'],
  ['GY', 'Guyana', '+592'],
  ['HT', 'Haiti', '+509'],
  ['HM', 'Heard Island and McDonald Islands', '+672'],
  ['VA', 'Holy See', '+39'],
  ['HN', 'Honduras', '+504'],
  ['HK', 'Hong Kong', '+852'],
  ['HU', 'Hungary', '+36'],
  ['IS', 'Iceland', '+354'],
  ['IN', 'India', '+91'],
  ['ID', 'Indonesia', '+62'],
  ['IR', 'Iran', '+98'],
  ['IQ', 'Iraq', '+964'],
  ['IE', 'Ireland', '+353'],
  ['IM', 'Isle of Man', '+44'],
  ['IL', 'Israel', '+972'],
  ['IT', 'Italy', '+39'],
  ['JM', 'Jamaica', '+1'],
  ['JP', 'Japan', '+81'],
  ['JE', 'Jersey', '+44'],
  ['JO', 'Jordan', '+962'],
  ['KZ', 'Kazakhstan', '+7'],
  ['KE', 'Kenya', '+254'],
  ['KI', 'Kiribati', '+686'],
  ['KP', "Korea, Democratic People's Republic of", '+850'],
  ['KR', 'Korea, Republic of', '+82'],
  ['KW', 'Kuwait', '+965'],
  ['KG', 'Kyrgyzstan', '+996'],
  ['LA', "Lao People's Democratic Republic", '+856'],
  ['LV', 'Latvia', '+371'],
  ['LB', 'Lebanon', '+961'],
  ['LS', 'Lesotho', '+266'],
  ['LR', 'Liberia', '+231'],
  ['LY', 'Libya', '+218'],
  ['LI', 'Liechtenstein', '+423'],
  ['LT', 'Lithuania', '+370'],
  ['LU', 'Luxembourg', '+352'],
  ['MO', 'Macao', '+853'],
  ['MG', 'Madagascar', '+261'],
  ['MW', 'Malawi', '+265'],
  ['MY', 'Malaysia', '+60'],
  ['MV', 'Maldives', '+960'],
  ['ML', 'Mali', '+223'],
  ['MT', 'Malta', '+356'],
  ['MH', 'Marshall Islands', '+692'],
  ['MQ', 'Martinique', '+596'],
  ['MR', 'Mauritania', '+222'],
  ['MU', 'Mauritius', '+230'],
  ['YT', 'Mayotte', '+262'],
  ['MX', 'Mexico', '+52'],
  ['FM', 'Micronesia', '+691'],
  ['MD', 'Moldova', '+373'],
  ['MC', 'Monaco', '+377'],
  ['MN', 'Mongolia', '+976'],
  ['ME', 'Montenegro', '+382'],
  ['MS', 'Montserrat', '+1'],
  ['MA', 'Morocco', '+212'],
  ['MZ', 'Mozambique', '+258'],
  ['MM', 'Myanmar', '+95'],
  ['NA', 'Namibia', '+264'],
  ['NR', 'Nauru', '+674'],
  ['NP', 'Nepal', '+977'],
  ['NL', 'Netherlands', '+31'],
  ['NC', 'New Caledonia', '+687'],
  ['NZ', 'New Zealand', '+64'],
  ['NI', 'Nicaragua', '+505'],
  ['NE', 'Niger', '+227'],
  ['NG', 'Nigeria', '+234'],
  ['NU', 'Niue', '+683'],
  ['NF', 'Norfolk Island', '+672'],
  ['MK', 'North Macedonia', '+389'],
  ['MP', 'Northern Mariana Islands', '+1'],
  ['NO', 'Norway', '+47'],
  ['OM', 'Oman', '+968'],
  ['PK', 'Pakistan', '+92'],
  ['PW', 'Palau', '+680'],
  ['PS', 'Palestine, State of', '+970'],
  ['PA', 'Panama', '+507'],
  ['PG', 'Papua New Guinea', '+675'],
  ['PY', 'Paraguay', '+595'],
  ['PE', 'Peru', '+51'],
  ['PH', 'Philippines', '+63'],
  ['PN', 'Pitcairn', '+64'],
  ['PL', 'Poland', '+48'],
  ['PT', 'Portugal', '+351'],
  ['PR', 'Puerto Rico', '+1'],
  ['QA', 'Qatar', '+974'],
  ['RE', 'Réunion', '+262'],
  ['RO', 'Romania', '+40'],
  ['RU', 'Russian Federation', '+7'],
  ['RW', 'Rwanda', '+250'],
  ['BL', 'Saint Barthélemy', '+590'],
  ['SH', 'Saint Helena, Ascension and Tristan da Cunha', '+290'],
  ['KN', 'Saint Kitts and Nevis', '+1'],
  ['LC', 'Saint Lucia', '+1'],
  ['MF', 'Saint Martin (French part)', '+590'],
  ['PM', 'Saint Pierre and Miquelon', '+508'],
  ['VC', 'Saint Vincent and the Grenadines', '+1'],
  ['WS', 'Samoa', '+685'],
  ['SM', 'San Marino', '+378'],
  ['ST', 'Sao Tome and Principe', '+239'],
  ['SA', 'Saudi Arabia', '+966'],
  ['SN', 'Senegal', '+221'],
  ['RS', 'Serbia', '+381'],
  ['SC', 'Seychelles', '+248'],
  ['SL', 'Sierra Leone', '+232'],
  ['SG', 'Singapore', '+65'],
  ['SX', 'Sint Maarten (Dutch part)', '+1'],
  ['SK', 'Slovakia', '+421'],
  ['SI', 'Slovenia', '+386'],
  ['SB', 'Solomon Islands', '+677'],
  ['SO', 'Somalia', '+252'],
  ['ZA', 'South Africa', '+27'],
  ['GS', 'South Georgia and the South Sandwich Islands', '+500'],
  ['SS', 'South Sudan', '+211'],
  ['ES', 'Spain', '+34'],
  ['LK', 'Sri Lanka', '+94'],
  ['SD', 'Sudan', '+249'],
  ['SR', 'Suriname', '+597'],
  ['SJ', 'Svalbard and Jan Mayen', '+47'],
  ['SE', 'Sweden', '+46'],
  ['CH', 'Switzerland', '+41'],
  ['SY', 'Syrian Arab Republic', '+963'],
  ['TW', 'Taiwan', '+886'],
  ['TJ', 'Tajikistan', '+992'],
  ['TZ', 'Tanzania', '+255'],
  ['TH', 'Thailand', '+66'],
  ['TL', 'Timor-Leste', '+670'],
  ['TG', 'Togo', '+228'],
  ['TK', 'Tokelau', '+690'],
  ['TO', 'Tonga', '+676'],
  ['TT', 'Trinidad and Tobago', '+1'],
  ['TN', 'Tunisia', '+216'],
  ['TR', 'Türkiye', '+90'],
  ['TM', 'Turkmenistan', '+993'],
  ['TC', 'Turks and Caicos Islands', '+1'],
  ['TV', 'Tuvalu', '+688'],
  ['UG', 'Uganda', '+256'],
  ['UA', 'Ukraine', '+380'],
  ['AE', 'United Arab Emirates', '+971'],
  ['GB', 'United Kingdom', '+44'],
  ['US', 'United States', '+1'],
  ['UM', 'United States Minor Outlying Islands', '+1'],
  ['UY', 'Uruguay', '+598'],
  ['UZ', 'Uzbekistan', '+998'],
  ['VU', 'Vanuatu', '+678'],
  ['VE', 'Venezuela', '+58'],
  ['VN', 'Viet Nam', '+84'],
  ['VG', 'Virgin Islands (British)', '+1'],
  ['VI', 'Virgin Islands (U.S.)', '+1'],
  ['WF', 'Wallis and Futuna', '+681'],
  ['EH', 'Western Sahara', '+212'],
  ['YE', 'Yemen', '+967'],
  ['ZM', 'Zambia', '+260'],
  ['ZW', 'Zimbabwe', '+263'],
];

/**
 * The alpha-2 code as regional indicator symbols, which is what a flag emoji
 * is — derived rather than typed out 250 times, so a `code` and its `flag` can
 * never disagree.
 */
const flagEmoji = (code) =>
  code.replace(/./g, (char) => String.fromCodePoint(0x1f1e6 + char.charCodeAt(0) - 65));

/** `{ code, name, flag, dialCode }`, ordered by name. */
export const COUNTRIES = COUNTRY_TUPLES.map(([code, name, dialCode]) => ({
  code,
  name,
  flag: flagEmoji(code),
  dialCode,
}));

/**
 * Countries we don't serve — the backend's BANNED_COUNTRIES, by ISO code. A
 * trader from one of them could register and only be refused at KYC, so the
 * sign-up Country list leaves them out (review doc, Login page).
 *
 * The backend list names a few by other spellings: "iran (islamic republic
 * of)" is IR, "syrian arab republic" SY, "venezuela (bolivarian republic of)"
 * VE, "republic of south sudan" SS, "democratic republic of congo" CD (not the
 * Republic of the Congo, CG), "north korea" KP, "russia" RU, "vietnam" VN.
 * "region of crimea" has no code of its own — it is part of Ukraine (UA), which
 * stays in the list.
 */
export const BANNED_COUNTRY_CODES = new Set([
  'AF', 'BY', 'BI', 'CF', 'ER', 'IR', 'IQ', 'LY', 'MM', 'SO', 'SD', 'SY', 'YE',
  'VE', 'VN', 'RU', 'SS', 'CD', 'KP', 'TD', 'CU',
]);

/** The sign-up Country list: every country we serve. */
export const SERVED_COUNTRIES = COUNTRIES.filter((c) => !BANNED_COUNTRY_CODES.has(c.code));

/** What the country and phone selects fall back to when nothing is chosen. */
export const DEFAULT_COUNTRY_CODE = 'GB';
export const DEFAULT_DIAL_CODE = '+44';

export const findCountry = (code) =>
  COUNTRIES.find((c) => c.code === code || c.name === code) ?? null;

/** The select holds a country code; the signup API wants the name ("India"). */
export const countryName = (code) => findCountry(code)?.name ?? code;

/**
 * Fourteen dial codes cover more than one territory, so which flag a bare
 * "+44" should show is a choice rather than a lookup — name it here instead of
 * letting list order decide (which would answer +44 with Guernsey and +1 with
 * American Samoa).
 */
const PRIMARY_BY_DIAL_CODE = {
  '+1': 'US',
  '+7': 'RU',
  '+39': 'IT',
  '+44': 'GB',
  '+47': 'NO',
  '+61': 'AU',
  '+64': 'NZ',
  '+212': 'MA',
  '+262': 'RE',
  '+358': 'FI',
  '+500': 'FK',
  '+590': 'GP',
  '+599': 'CW',
  '+672': 'AQ',
};

/** The country a dial code should be shown as when that's all we're holding. */
export const findCountryByDialCode = (dialCode) => {
  const primary = PRIMARY_BY_DIAL_CODE[dialCode];
  if (primary) return findCountry(primary);
  return COUNTRIES.find((c) => c.dialCode === dialCode) ?? null;
};
